import { FC, useCallback, useEffect, useState } from "react"
import { RecurrentProductService } from "../../../../../services/recurrentProducts/recurrentProducts";
import { useParams } from "react-router-dom";
import useFilters from "../../../../../hooks/useFilters";
import useFetch from "../../../../../hooks/useFetch";
import { toast } from "sonner";
import useHandleErrors from "../../../../../hooks/useHandleErrors";
import Page from "../../../../../layout/Page/Page";
import CustomTable from "../../../../../components/table/CustomTable";
import { IoIosAdd } from "react-icons/io";
import { ActionIcon, Button, Tooltip } from "rizzui";
import { Link } from "react-router-dom";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { menuRoutes } from "../../../../../router/menu";
import DeletePopover from "../../../../../components/buttons/DeletePopover";
import { FormDrawerView } from "../../../../../components/forms/FormDrawer";
import RecurrentProductsForm from "./components/RecurrentProductsForm";

interface HeadquarterProductProps {
}

const columnOrder = [
    'id',
    'company',
    'headquarter',
    'product',
    'estimatedQuantity',
    'comments'
]

const HeadquarterProduct: FC<HeadquarterProductProps> = () => {
    
    // STATES

    const [openFormDrawer, setOpenFormDrawer] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const service = new RecurrentProductService();

    // HOOKS
    
    const { id = '' } = useParams<{ id: string }>();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFilters();
    const { handleErrors } = useHandleErrors();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE LOS PRODUCTOS HABITUALES DE UNA SEDE
     * @EN GETS THE RECURRENT PRODUCTS OF A HEADQUARTER
     * 
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const newFilters = {
            ...filters,
            filter_filters: {
                ...filters.filter_filters,
                headquarter: id
            }
        }
        const response = await service.listRecurrentProducts(newFilters);
        return response.getResponseData();
    }, [filters]))
    // -------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR MULTIPLES PRODUCTOS HABITUALES
     * @EN DELETES MULTIPLE RECURRENT PRODUCTS
     * 
     * @param ids - ARRAY DE ID'S DE LOS PRODUCTOS HABITUALES A ELIMINAR
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultipleDelete = async (ids: string[]) => {
        try {
            const response = (await service.deleteMultiRecurrentProduct(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Productos habituales eliminados correctamente');
            } else {
                handleErrors(response);

            }
        } catch (error) {
            handleErrors(error);
        }
    }
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELIMINACION DE UN PRODUCTO HABITUAL
     * @EN HANDLES THE DELETION OF A RECURRENT PRODUCT
     * 
     * @param id - ID DEL PRODUCTO HABITUAL A ELIMINAR
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = (await service.deleteRecurrentProduct(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Producto habitual eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    }
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'recurrent-products-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    isSortable
                    children={
                        <Button
                           variant="solid"
                           color="primary"
                           className="flex items-center"
                           onClick={() => { 
                               setOpenFormDrawer(true);
                               setSelectedProduct(null);
                           }}
                       >
                           <IoIosAdd className="h-[20px] w-[20px] mr-2" />
                           Añadir
                       </Button>
                    }
                    columnsNoSortables={[]}
                    columnsNotShown={[]}
                    overrideColumns={[
                        {
                            key: 'company',
                            columnNameKey: 'Empresa',
                            permissions: [{
                                group: 'company',
                                permission: 'list'
                            }],
                            render: (row: any) => {
                                if (!row.row.original?.company) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.company?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'headquarter',
                            columnNameKey: 'Sede',
                            render: (row: any) => {
                                if (!row.row.original?.headquarter) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.headquarter?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'product',
                            columnNameKey: 'Producto',
                            render: (row: any) => {
                                if (!row.row.original?.product) return 'N/A';
                                return (
                                    <div>
                                        <Link to={`${menuRoutes.products.profile}/${row.id}`}>
                                            {row.row.original?.product?.name}
                                        </Link>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'estimatedQuantity',
                            columnNameKey: 'Cantidad estimada',
                            render: (row: any) => {
                                if (!row.row.original?.estimatedQuantity) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.estimatedQuantity}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'comments',
                            columnNameKey: 'comentarios',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.comments}
                                    </div>
                                );
                            }
                        },
                    ]}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'provider_headquarter_recurrent_products', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar producto habitual'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setSelectedProduct(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'provider_headquarter_recurrent_products', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar producto habitual'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar producto habitual`}
                                                description={`¿Estás seguro de que deseas eliminar el producto habitual?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    handleMultipleDelete={handleMultipleDelete}
                />
            )}


            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    selectedProduct ?
                        "Completa el formulario para editar los datos del producto habitual" :
                        "Completa el formulario para crear un nuevo producto habitual"
                }
                drawerTitle={selectedProduct ? "Editar producto habitual" : "Crear producto habitual"} submitText={
                    selectedProduct ? "Editar producto habitual" : "Crear producto habitual"
                }>
                    <RecurrentProductsForm setOpenModal={setOpenFormDrawer} refetch={refetch} headquarterId={id}  product={selectedProduct} />
            </FormDrawerView>
            
        </Page>
    )
}

export default HeadquarterProduct;